import Select from "react-select";
import { components } from "react-select";
import { Box, Text } from "@chakra-ui/react";

export interface Option {
  id: string;
  name: string; // New field for search
  component: JSX.Element;
}

export const DropDown = ({
  selected,
  title,
  options,
  onOptionChange,
  placeHolder,
  fontSize = "14px",
  disabled = false,
  isMulti = false,
}: {
  selected: Option | Option[] | undefined;
  title: JSX.Element;
  options: Option[];
  onOptionChange: (selectedOptions: Option | Option[]) => void;
  placeHolder?: string;
  fontSize?: string;
  disabled?: boolean;
  isMulti?: boolean;
}) => {
  // Map options to the format react-select expects
  const selectOptions = options.map((option) => ({
    value: option.id, // Use id as the value for consistency
    label: option.component,
    searchLabel: option.name, // New field for search
  }));

  // Convert selected option(s) to the format react-select expects
  const selectedOptions = selected
    ? Array.isArray(selected)
      ? selected.map((option) => ({
          value: option.id,
          label: option.component,
        }))
      : { value: selected.id, label: selected.component }
    : null;

  // Custom single value component
  const SingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props}>
      <Box
        fontSize={fontSize}
        fontWeight="500"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {children}
      </Box>
    </components.SingleValue>
  );

  // Custom filter logic to allow searching by `searchLabel`
  const customFilter = (option: any, inputValue: string) =>
    option.data.searchLabel?.toLowerCase().includes(inputValue.toLowerCase());

  return (
    <Box width="100%">
      {title}
      <Select
        value={selectedOptions}
        onChange={(selectedOptions: any) => {
          console.log("Selected options:", selectedOptions);
          if (isMulti) {
            onOptionChange(
              selectedOptions.map((option: any) => ({
                id: option.value,
                name: option.searchLabel, // Pass original searchLabel back
                component: option.label,
              }))
            );
          } else {
            onOptionChange({
              id: selectedOptions.value,
              name: selectedOptions?.data?.searchLabel || "", // Pass original searchLabel back
              component: selectedOptions.label,
            });
          }
        }}
        options={selectOptions}
        isMulti={isMulti}
        isDisabled={disabled}
        components={{ SingleValue }}
        filterOption={customFilter} // Use custom filter logic
        styles={{
          control: (provided) => ({
            ...provided,
            borderWidth: "1px",
            borderColor: "rgba(255, 255, 255, 0.2)",
            backgroundColor: disabled ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            cursor: disabled ? "not-allowed" : "pointer",
            padding: "2px",
            width: "100%",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              borderColor: "rgba(255, 255, 255, 0.3)",
            },
          }),
          menu: (provided) => ({
            ...provided,
            maxHeight: "40vh",
            zIndex: 9999,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(20px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            color: "white",
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: fontSize,
            color: "white",
          }),
          option: (provided, state) => ({
            ...provided,
            cursor: "pointer",
            backgroundColor: state.isFocused ? "rgba(118, 82, 247, 0.3)" : "transparent",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(118, 82, 247, 0.3)",
            },
          }),
          placeholder: (provided) => ({
            ...provided,
            fontSize: fontSize,
            color: "rgba(255, 255, 255, 0.6)",
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            color: "white",
            "&:hover": {
              color: "rgba(118, 82, 247, 0.8)",
            },
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }),
        }}
        placeholder={
          placeHolder
            ? placeHolder
            : isMulti
            ? "Select option(s)"
            : "Select option"
        }
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: "rgba(118, 82, 247, 0.3)",
            primary: "#7652F7",
            neutral0: "rgba(0, 0, 0, 0.8)",
            neutral10: "rgba(255, 255, 255, 0.1)",
            neutral20: "rgba(255, 255, 255, 0.2)",
            neutral30: "rgba(255, 255, 255, 0.3)",
            neutral40: "rgba(255, 255, 255, 0.4)",
            neutral50: "rgba(255, 255, 255, 0.5)",
            neutral60: "rgba(255, 255, 255, 0.6)",
            neutral70: "rgba(255, 255, 255, 0.7)",
            neutral80: "white",
            neutral90: "white",
          },
        })}
        noOptionsMessage={() => (
          <Text fontSize="16px" textColor="rgba(255, 255, 255, 0.6)" my={2}>
            No options available
          </Text>
        )}
        menuPortalTarget={document.body}
      />
    </Box>
  );
};
